import { A11yModule } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	inject,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatterSource } from '@models/matter';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { MatterWordDocumentsDispatch } from '@state/matter/matter-word-documents/matter-word-documents.dispatch';
import { MatterWordDocumentWithMetadata } from '@state/matter/matter-word-documents/matter-word-documents.selectors';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { omit } from 'lodash';
import { Observable, Subscription, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, take } from 'rxjs/operators';
import {
	AnalyticsContext,
	DocListItemComponent,
} from 'src/app/ui/doc-list-item/doc-list-item.component';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { DropdownItem } from 'src/app/ui/dropdown/dropdown.component';
import { ColSpanDirective } from 'src/app/ui/grid/col-span.directive';
import { IconsModule } from 'src/app/ui/icons/icons.module';
import { DocPreviewModalComponent } from '../doc-preview-modal/doc-preview-modal.component';
import { DocumentsDrawerComponent } from '../documents-drawer/documents-drawer.component';
import { DocumentsV2Selectors } from '../documents-v2.selectors';

import {
	SortDirection,
	SortField,
	WordDocumentsViewModel,
} from './word-documents.view-model';
import { ButtonComponent } from 'src/app/ui/button/button.component';
import { TestHookDirective } from '@core/ui';
import { TestHooks } from './word-documents.hooks';

import { DocumentCategory } from '@models/matter-word-documents';
import { PdfModalComponent } from 'src/app/shared/components/pdf-modal/pdf-modal.component';
import { ModalFormInstanceViewerComponent } from 'src/app/shared/components/matter-form-instance-viewer/modal-matter-form-instance-viewer/modal-matter-form-instance-viewer.component';
import { MatterFormService } from 'src/app/shared/services/matter-form/matter-form.service';

interface MyDocument extends MatterWordDocumentWithMetadata {
	isNew: boolean;
	isEditing?: boolean;
}

@Component({
	selector: 'app-word-documents',
	imports: [
		CommonModule,
		ColSpanDirective,
		IconsModule,
		MatTooltipModule,
		A11yModule,
		DocListItemComponent,
		ReactiveFormsModule,
		ButtonComponent,
		TestHookDirective,
	],
	templateUrl: './word-documents.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [WordDocumentsViewModel],
})
export class WordDocumentsComponent implements OnInit, OnDestroy {
	testHooks = TestHooks;
	readonly viewModel = inject(WordDocumentsViewModel);
	searchFormControl = new FormControl('');
	private matterFormService = inject(MatterFormService);

	@Select(DocumentsV2Selectors.getDocumentsWithStatusesAndPackages)
	documents$: Observable<MatterWordDocumentWithMetadata[]>;

	@SelectSnapshot(SelectedMatter.matterSource) matterSource: MatterSource;
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	private documentsSubscription: Subscription;
	private readonly NEW_DOCUMENT_DELAY = 700;
	private isFirstLoad = true;
	private previousDocumentIds = new Set<string>();
	@ViewChild('titleInput') titleInput: ElementRef<HTMLInputElement>;
	readonly ANALYTIC_CONTEXT = AnalyticsContext;

	readonly documents: ReturnType<
		typeof toSignal<MatterWordDocumentWithMetadata[]>
	>;

	constructor(
		private drawerService: DrawerService,
		private matterWordDocumentDispath: MatterWordDocumentsDispatch,
	) {
		this.documents = toSignal(this.documents$, { initialValue: [] });
	}

	ngOnInit(): void {
		this.searchFormControl.valueChanges
			.pipe(debounceTime(300), distinctUntilChanged())
			.subscribe((query) => {
				this.viewModel.updateSearchQuery(query || '');
			});

		this.documentsSubscription = this.documents$
			.pipe(
				map((docs) =>
					docs.filter(
						(doc) =>
							(!doc.document.categories?.includes(DocumentCategory.UPLOAD) &&
								!doc.document.categories?.includes(
									DocumentCategory.SYNC_FORMS,
								) &&
								!doc.document.categories?.includes(
									DocumentCategory.MATTER_FORMS,
								)) ||
							doc.includedInPackages.length > 0,
					),
				),
			)
			.subscribe((documentsWithStatusAndPackages) => {
				if (this.isFirstLoad) {
					this.previousDocumentIds = new Set(
						documentsWithStatusAndPackages.map((doc) => doc.id),
					);
					this.isFirstLoad = false;
					this.viewModel.updateDocuments(
						documentsWithStatusAndPackages.map((doc) => ({
							...doc,
							isNew: false,
						})),
					);
					return;
				}

				const documents = documentsWithStatusAndPackages.map((doc) => {
					const isNew = !this.previousDocumentIds.has(doc.id);

					if (isNew) {
						timer(this.NEW_DOCUMENT_DELAY)
							.pipe(take(1))
							.subscribe(() => {
								this.previousDocumentIds.add(doc.id);
								const updatedDocs = this.viewModel.documents().map((d) => ({
									...d,
									isNew: d.id === doc.id ? false : d.isNew,
								}));
								this.viewModel.updateDocuments(updatedDocs);
							});
					}

					return {
						...doc,
						isNew,
					};
				});

				this.viewModel.updateDocuments(documents);
			});
	}

	items: DropdownItem[] = [];

	ngOnDestroy(): void {
		if (this.documentsSubscription) {
			this.documentsSubscription.unsubscribe();
		}
	}

	onDocumentSelected(docMeta: MatterWordDocumentWithMetadata) {
		this.matterWordDocumentDispath.Create({
			document: docMeta.document,
		});
	}

	openDocumentsDrawer() {
		this.drawerService.openDrawer(DocumentsDrawerComponent, {
			title: 'Documents',
			data: {
				content: this.viewModel.documents(),
				onDocumentSelected: this.onDocumentSelected.bind(this),
			},
			noStyle: true,
		});
	}

	async onDocumentClick(docMeta: MyDocument) {
		if (docMeta.document.categories?.includes(DocumentCategory.UPLOAD)) {
			this.drawerService.openDrawer(PdfModalComponent, {
				title: docMeta.document.title,
				data: {
					fileName: docMeta.document.title,
					fileId: docMeta.document.id,
					googleSrcUrl: docMeta.document.sourceStorageRef,
					showDelete: true,
				},
				noStyle: true,
			});
		} else if (
			docMeta.document.categories?.includes(DocumentCategory.SYNC_FORMS) ||
			docMeta.document.categories?.includes(DocumentCategory.MATTER_FORMS)
		) {
			const form = await this.matterFormService.getMatterFormInstance(
				docMeta.document.id,
			);
			this.drawerService.openDrawer(ModalFormInstanceViewerComponent, {
				title: docMeta.document.title,
				data: { form },
				noStyle: true,
			});
		} else {
			this.drawerService.openDrawer(DocPreviewModalComponent, {
				title: docMeta.document.title,
				data: { docMeta: docMeta },
				noStyle: true,
			});
		}
	}

	onDocumentDuplicate(docMeta: MyDocument) {
		this.matterWordDocumentDispath.Duplicate(docMeta.document);
	}

	trackByDocumentId(index: number, document: MyDocument): string {
		return document.id;
	}

	removeLoadingStatesFromDoc = (doc: MyDocument) =>
		omit(doc, [
			'isLoading',
			'isResetting',
			'isDeleting',
			'isDuplicating',
			'isNew',
			'isEditing',
			'isOpening',
			'includedInPackages',
			'isExporting',
		]);

	onTitleClick(document: MyDocument) {
		this.viewModel.updateDocuments(
			this.viewModel.documents().map((doc) => ({
				...doc,
				isEditing: doc.id === document.id,
			})),
		);

		setTimeout(() => {
			this.titleInput?.nativeElement?.focus();
		});
	}

	onTitleBlur(docMeta: MyDocument, newTitle: string) {
		if (newTitle.trim() === docMeta.document.title || newTitle.trim() === '') {
			this.viewModel.updateDocuments(
				this.viewModel.documents().map((doc) => ({
					...doc,
					isEditing: false,
				})),
			);
			return;
		}

		const updatedDocMeta = {
			...docMeta,
			document: {
				...docMeta.document,
				title: newTitle.trim(),
			},
		};

		this.viewModel.updateDocuments(
			this.viewModel.documents().map((doc) => ({
				...doc,
				title:
					doc.id === docMeta.id
						? updatedDocMeta.document.title
						: doc.document.title,
				isEditing: false,
			})),
		);

		this.matterWordDocumentDispath.UpdateDocument(updatedDocMeta.document);
	}

	onOpenDocumentInWord(docMeta: MyDocument) {
		this.matterWordDocumentDispath.OpenDocumentInWord(docMeta.document);
	}

	onSort(field: SortField): void {
		this.viewModel.toggleSort(field);
	}

	getSortDirection(field: SortField): SortDirection {
		return this.viewModel.sortDirections()[field];
	}

	getSortIcon(field: SortField): string {
		return this.viewModel.sortIcons()[field];
	}
}
